/**
 * Canonical line-box contract for every favicon/name or favicon/URL identity
 * embedded in report prose. The text item participates in the flex baseline,
 * so the identity's label shares the surrounding sentence's baseline. Marks
 * opt into `self-center` at each inline call site, centering the artwork within
 * that text line without letting the image's bottom edge define the baseline.
 * This constant lives outside client components so server-rendered source links
 * can use the same layout.
 */
export const INLINE_IDENTITY_LAYOUT = 'inline-flex items-baseline align-baseline';

export const verdictText = {
  headline: 'text-[clamp(1.5rem,2.2vw,2rem)] font-semibold leading-[1.15] tracking-[-0.015em]',
  pullQuote: 'text-[clamp(1.5rem,2.6vw,2rem)] font-semibold leading-[1.25] tracking-[-0.02em]',
  rebuttal: 'text-sm sm:text-base font-normal leading-[1.6]',
  attribution: 'text-detail font-medium tracking-[0.03em]',
  score: 'text-[2.5rem] sm:text-[3.25rem] font-semibold leading-none tracking-[-0.03em]',
  scoreMax: 'text-lg font-medium',
  scoreSub: 'text-sm',
  eyebrow: 'text-detail font-medium tracking-[0.02em]',
};

/**
 * Body-level text register.
 *
 * Weight hierarchy (heaviest → lightest):
 *   semibold (600) — the DISPLAY section title only (rule 61: semibold is a
 *                     display weight, `text-lg` and up; `subsectionTitle` is
 *                     14px, so it is medium)
 *   medium   (500) — row labels (names), micro-labels, badges
 *   normal   (400) — metric values (%, counts), descriptions, meta
 *
 * outcome-win is brand teal (set in index.css / globals.css).
 */
export const bodyText = {
  sectionTitle: 'text-[1.625rem] font-semibold tracking-[-0.02em] text-ink-strong',
  sectionDescription: 'text-sm leading-relaxed text-ink-content',
  subsectionTitle: 'text-sm font-medium tracking-[0.01em] text-ink-strong',
  microLabel: 'text-xs font-medium text-ink-supporting tracking-[0.01em]',
  rowTitle: 'text-sm font-medium text-ink-content',
  meta: 'text-xs text-ink-supporting leading-relaxed',
  badge: 'text-micro font-medium',
  /** Metric values (percentages, counts). Normal weight + tabular nums. */
  metric: 'text-sm tabular-nums',
};

/**
 * Editorial report register for long-form public reports — THE canonical type
 * scale for hosted reports (see max-design-rules rule 52). Imported only by the
 * report components; every piece of text in a report maps to exactly one role
 * here, so the document reads uniform (rule 20: same size + weight, color is the
 * only differentiator).
 *
 * Eight roles, deliberately minimal:
 *   5 sizes   — display clamp / section clamp / base (body, subsection) / sm (support) / xs (meta)
 *   3 weights — normal (body, support, meta) / medium (quote, emphasis) /
 *               semibold (display, sectionTitle, subsectionTitle)
 *   3 inks    — strong (structure), content (everything worth reading), and
 *               supporting (labels, metadata and explanatory chrome)
 *
 * `support` joined 2026-07-29 (Max: the finding cards were "too loud… text thrown
 * everywhere"): the one step between body and meta, for the interpretive sentence
 * under a finding's headline. De-emphasis by SIZE+COLOR together is what lets the
 * findings list drop its card chrome without the levels collapsing into each other.
 *
 * Emphasis is the ONE inline emphasis mechanism (no italics, no value color-
 * coding): it sets weight + color only, so it inherits the local size and works
 * inside both body and meta. Prose is left-aligned with `text-pretty` (never
 * justified — justify rivers on the web). The only sanctioned exceptions are the
 * SVG chart's internal `text-micro` labels and the one `font-mono` equation.
 */
export const editorialText = {
  /** The single hero <h1>. */
  display:
    'text-balance text-[clamp(2.25rem,4vw,3rem)] font-semibold leading-[1.08] tracking-[-0.03em] text-ink-strong',
  /**
   * THE one intermediate display size, between `display` and `sectionTitle` —
   * a verbatim pull quote and the closing statement, and nothing else.
   *
   * It exists to DELETE sizes, not add one. The brief was running two separate
   * ad-hoc clamps at this tier — `PullQuoteReceipt`'s
   * `text-[clamp(1.15rem,1.9vw,1.5rem)]` and the closing card's
   * `text-[clamp(1.3rem,2.2vw,1.75rem)]` — which are two different sizes doing
   * one job, two screens apart, and both were `text-[...]` literals of exactly
   * the kind the type-token rule bans outside a hero headline. One role, one
   * clamp, two call sites.
   *
   * `font-medium`, not semibold: rule 61 gates semibold to `text-lg` and up, and
   * while the clamp's ceiling clears that, its floor (18.4px) does not — a role
   * that changes weight class as the viewport narrows is not a role. Medium also
   * keeps it reading as EVIDENCE FOR the verdict above it rather than a second
   * headline, which is the note that shrank the pull quote in the first place.
   */
  /*
   * NO `text-balance` (2026-07-31). It was here, and it is what Max saw: *"one of
   * the quotes is still text wrapping too early."*
   *
   * `text-wrap: balance` evens out line lengths by making every line SHORTER than
   * the container. Measured on the live brief at a 1032px container, grouping the
   * blockquote's client rects by `top` to get true per-line extents: balanced it
   * ran 782 / 801 / 855 while the body prose beside it reached 994 / 1007 / 1016.
   * A ~190px shortfall on the one element the eye is drawn to reads as a stray
   * indent, not as typographic care.
   *
   * NOTE FOR THE NEXT PERSON: an element-width check will NOT catch this. The box
   * is a full 1032px in every case — only the LINE extents differ. Measure with
   * `Range.getClientRects()` grouped by `top`, never `getBoundingClientRect()` on
   * the element. Two verification passes missed it exactly that way.
   *
   * `editorialText.display` KEEPS `text-balance`: measured, it changes nothing on
   * the current headline, and balancing a two-line display headline is the case
   * the property was designed for.
   */
  /** AN EXTRACTED QUOTE — a verbatim sentence an AI told a buyer.
   *
   *  TABLE-VALUE SIZE. Not a display role at all (2026-08-14): examples now use the
   *  same productive 13px/18px register as the report's data rows. Their medium weight,
   *  quotation marks and contained evidence surface preserve the quote role without
   *  letting a long excerpt visually outrank the narrative around it.
   *
   *  A quote is content, not a separate color tier. Weight, quotation marks and
   *  the surrounding hairline identify it without making adjacent prose flicker
   *  between dark and gray.
   *
   *  The closing card's "This is one week of one benchmark." NO LONGER shares this: it
   *  is a heading for its card, not an extracted quote, and it uses `sectionTitle` like
   *  every other heading in the document. That is the genuine second role the previous
   *  comment said would earn its own treatment. */
  quote: 'text-table font-medium tracking-[-0.005em] text-ink-content',
  /** <h2> section headers. */
  sectionTitle: 'text-[clamp(1.375rem,2.2vw,1.625rem)] font-semibold leading-[1.2] tracking-[-0.02em] text-ink-strong',
  /** <h3>/<h4> headings within an act. Kept distinct from extracted quotes so
   * semantic headings no longer borrow evidence styling. */
  subsectionTitle: 'text-base font-semibold leading-snug tracking-[-0.01em] text-ink-strong',
  /** All prose, list items, quotes, evidence, insights.
   *
   *  `leading-relaxed` (1.625), NOT `leading-7` (2026-07-31). At 16px, `leading-7`
   *  is a 28px line box — a 1.75 ratio — and Max, on the rendered brief: *"it looks
   *  like the line height of the individual text is very big. Like, it looks like we
   *  have a new line in between every single line of text."* He is describing 1.75
   *  exactly. The old value was defensible when this document was long-form prose on
   *  a wide measure (long lines want more leading to hold the return), but the brief
   *  is now short paragraphs between exhibits, and at that length 1.75 reads as
   *  double-spacing rather than as air. */
  body: 'text-base leading-relaxed text-ink-content',
  /** The interpretive sentence under a finding's headline (the so-what) — one quiet
   *  step below body, one above meta. Never for primary prose.
   *
   *  `leading-relaxed`, NOT `leading-6` (2026-07-31). At 14px, `leading-6` is a 24px
   *  line box — a 1.714 ratio, which is the same "*it looks like we have a new line in
   *  between every single line of text*" complaint that moved `body` off `leading-7`,
   *  one notch quieter. It survived that pass because the fix was applied to the token
   *  Max was pointing AT rather than to every token in the family, and it is visible on
   *  the live brief right now ("Gemini told a buyer this."). The whole register now
   *  shares one prose ratio. */
  support: 'text-sm leading-relaxed text-ink-supporting',
  /** The ONE INLINE emphasis — key numbers + a few pivotal terms, INSIDE a sentence.
   *
   *  WEIGHT ONLY. NO COLOUR (2026-07-31). It previously used the strong ink,
   *  so every emphasized number rendered BOTH heavier and brighter than the
   *  words around it. Max, on "We did
   *  that to all 1,143 answers": *"Why is the 1,143 a different font color than the
   *  rest of the text? … the only thing that should be a different color should maybe
   *  be the section headers and the section numbering."*
   *
   *  Inheriting the colour means an inline emphasis changes exactly ONE thing about
   *  the run of text it sits in, which is all emphasis needs to do. A number that
   *  shifts hue mid-sentence reads as a link or an error before it reads as emphasis.
   *
   *  MEDIUM, not semibold, since 2026-07-29 — the brief runs this span dozens of
   *  times through running prose and every semibold read as a shout (Max: "a ton of
   *  different font weights… unify these so everything is less loud").
   *
   *  For a whole paragraph that OPENS a beat, use `lead` below — that is a block, not
   *  a mid-sentence shift, and it is the reason this token used to carry ink at all. */
  emphasis: 'font-medium',
  /** THE MASTHEAD IDENTITY — the company name beside its mark, and nothing else.
   *
   *  This was `lead`, applied to any paragraph opening a beat. It is the one
   *  non-heading identity that belongs in strong ink.
   *
   *  Max, looking at "One RDI Corp AI Simulator page … is shaping what AI tells buyers"
   *  rendered primary ink as the first half of an otherwise ordinary paragraph:
   *  *"below it should not be strong white because that's just embedded in a regular
   *  paragraph of text… we can clearly tell whenever we use these like strong white
   *  that it is an example."* A weight-and-ink change part-way through a paragraph
   *  reads as a different KIND of content, and a lead sentence is not a different kind
   *  of content — it is the paragraph starting.
   *
   *  Every lead sentence in the document remains plain content; the act headers
   *  already provide the necessary wayfinding. */
  identity: 'font-medium text-ink-strong',
  /** Tiny labels/captions only (chip labels, demand line, caveat, methodology). */
  meta: 'text-xs leading-relaxed text-ink-supporting',
  /** Inline links. The hover is the UNDERLINE alone — a `hover:bg-card` was
   *  here until 2026-07-26 and painted nothing, because report prose already
   *  sits on `bg-card` (rule 7: underline, never a background highlight). */
  /** An inline link in report prose.
   *
   *  `px-0.5 -mx-0.5` — the padding gives the focus ring and hover underline a little
   *  air around the text; the equal negative margin then takes that space back out of
   *  the LAYOUT, so the link occupies exactly the width of its own words.
   *
   *  It was `px-0.5` alone until 2026-08-04, and the 2px it added showed up as a
   *  visible gap before any punctuation that followed a link: the brief rendered
   *  "The rest are in Evidence ." and "See the other 20 in Evidence ." — a
   *  space before a full stop, which is the classic typo tell, in a document whose
   *  whole proposition is that we are precise. It also pushed a caption that OPENED on
   *  a link 2px off the inset every sibling caption sat on.
   *
   *  Padding-plus-negative-margin is the standard way to keep a focus affordance
   *  without paying for it in flow. Do not drop either half: `px-0.5` alone reopens the
   *  gap, and `-mx-0.5` alone clips the ring into the neighbouring words. */
  link: 'rounded-sm px-0.5 -mx-0.5 text-primary no-underline transition-colors hover:underline hover:decoration-primary/40 hover:underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
} as const;

/**
 * Stat-cell register — THE canonical big-number-over-label KPI cell.
 *
 * Single home for the value + label sizing that six components historically
 * re-rolled at slightly different sizes (MetricsStrip `MetricStat`,
 * `KpiMetricCell`, `AiPresenceMetrics`, Sources `ShareMetric`, `SourceScorecard`,
 * Playbook `EconCell`). The shared `StatCell` consumes these; never hardcode
 * `text-2xl` / `text-detail` for a KPI block again.
 *
 * `text-detail` lives here intentionally — Tailwind has no 11px step and the
 * shared preset can't extend `fontSize` (TS preset-inference conflict), so the
 * micro-label size is centralized in this register rather than inlined per call.
 */
export const statText = {
  /**
   * THE metric value — 16px at `font-medium`. There is exactly ONE KPI value
   * size in the product (rule 63, 2026-07-26).
   *
   * A metric summarising the table beneath it is ORIENTATION, not emphasis: it
   * must read as typographically SUBORDINATE to the data it summarises. It was
   * `text-3xl` (30px semibold) and Max, on two screenshots: *"the 22, 3, 24, and
   * 0 … seem like they are kind of like floating and randomly there, and the
   * numbers seem too big. How can we make these more minimal and thin?"*
   * Measured against peec.ai — the reference he named — numeric values render at
   * 12px/weight-500 dominant with 16px the MAXIMUM. We rendered 30px above a
   * 13px table. 16px/500 is the top of peec's range, which keeps the value
   * legible while it stops shouting; the weight also satisfies rule 61 (semibold
   * is a display weight, `text-lg` and up — 16px takes `font-medium`).
   *
   * StatCell's value row is `h-6` so the 24px line-height doesn't clip.
   *
   * THREE other steps used to sit around this one and all THREE are deleted.
   * `valueSm`/`valueXs` went first (2026-07-26 morning): once the headline
   * dropped to 16px there was nothing left for them to be smaller THAN, and
   * `valueXs` (`text-base font-semibold`) was a live rule-61 violation. Then
   * `valueHero` (`text-3xl`, `StatCell size="hero"`), which that same pass kept
   * as a "display exception" on the `/home` header and the period header —
   * Max, looking at the period header hours later: *"The KPIs, like the font,
   * are larger for the values here compared to other pages. We should
   * standardize and make this consistent everywhere, shouldn't we?"* A
   * two-site exception IS the inconsistency the reader sees; a KPI tile is the
   * same object on every page, so it renders at the same size on every page.
   * Do not reintroduce a display tier — if a number needs to be the page's
   * subject, that is a headline, not a tile.
   */
  value: 'text-base font-medium tabular-nums text-ink-content',
  /** Micro-label paired with the value (sentence case — matches existing KPI strips). */
  label: 'text-xs font-medium tracking-[0.01em] text-ink-supporting',
  /** Delta / sub-note beside the value (color applied by caller, exception-only). */
  delta: 'text-xs font-medium tabular-nums',
} as const;

/**
 * productText — THE type register for the PRODUCT register (console + internal).
 *
 * The sibling of `editorialText` (which owns the hosted/brand register). Same
 * discipline, different register: dense, utilitarian, fixed-width type.
 *
 * Why this exists: an audit found **38 distinct size/weight combinations** in the
 * console with ~18 of them one-off inventions, and a hierarchy so flat that the
 * page `<h1>` (`text-xl`, 20px) barely outranked a breadcrumb crumb and a
 * `SectionHeader` (`text-base`, 16px). Nothing anchored a page. Map every piece
 * of chrome text to ONE role here instead of inventing classes inline.
 *
 * Budget: 5 sizes (2xl / base / sm / xs / detail), 2 weights (medium/semibold),
 * and 3 semantic inks. Strong belongs to structure, content to prose and data,
 * and supporting to labels and metadata.
 *
 * WEIGHT IS SIZE-GATED (calm pass, 2026-07-26 — design rule 61): **semibold is a
 * DISPLAY weight, `text-lg` and up.** In this register that is `pageTitle` and
 * nothing else. At `text-base` and below the emphasis weight is `font-medium`,
 * so `sectionTitle`/`sectionTitleLg` are medium — at 14/16px semibold adds ink
 * without adding hierarchy, and measured against peec.ai we were running weight
 * 600 on 12% of text nodes to their 3%. A section heading is distinguished from
 * a cell by POSITION (it sits alone above its content) and by colour, not by
 * out-weighing it. Do not "restore" semibold here to make a heading feel
 * stronger — that is exactly the loudness this gate removes.
 *
 * Negative tracking scales with size (bigger type needs tighter tracking).
 * Optical sizing is automatic: `font-optical-sizing: auto` is set globally in
 * base.css and Inter's `opsz` axis (14→32) is loaded, so a 24px title gets a
 * genuinely different optical cut from 14px body text for free.
 *
 * NOTE: `tabular-nums` works (the shipped Inter build has `tnum`), but
 * `slashed-zero` does NOT — that build has no `zero` feature. Don't add it.
 */
export const productText = {
  /** The ONE page `<h1>`, in UnifiedPageLayout's title row. Must clearly outrank
   *  the breadcrumb beside it and every section heading below it. */
  pageTitle: 'text-3xl font-semibold leading-tight tracking-[-0.035em] text-ink-strong',
  /** A breadcrumb crumb in the title row — chrome, so it steps back from the
   *  title rather than competing with it (it used to render at 16px vs the
   *  title's 20px, which is why neither read as dominant). */
  crumb: 'text-sm font-medium text-ink-content',
  /** The `/` between crumbs. */
  crumbSeparator: 'text-sm text-ink-supporting',
  /** In-content `<h2>` — the default SectionHeader. */
  sectionTitle: 'text-base font-semibold tracking-[-0.015em] text-ink-strong',
  /** A LARGER in-content `<h2>` (SectionHeader size="lg") — for a section that
   *  leads a whole tab, not a sub-block. It outranks `sectionTitle` by SIZE
   *  (16 vs 14px); both are medium, per the weight gate above. */
  sectionTitleLg: 'text-lg font-semibold tracking-[-0.02em] text-ink-strong',
  /** Default body/prose inside the product register. */
  body: 'text-sm text-ink-content',
  /** Table cell — the identifier/name column. */
  cellId: 'text-sm font-medium text-ink-content',
  /** Table cell — every other data value. */
  cell: 'text-sm text-ink-content',
  /** Any number, anywhere: cells, inline stats, counts. Pairs with `cell`. */
  numeric: 'tabular-nums',
  /** A form/stat label or column-ish label. */
  label: 'text-xs font-medium text-ink-supporting',
  /** Captions, hints, timestamps, supporting annotations. */
  meta: 'text-detail text-ink-supporting',
} as const;

/**
 * Report card chrome — THE canonical container for report sections.
 *
 * The layer is part of the component's meaning, not decoration:
 *
 *   PANEL   `bg-card` + `border-border/60` — raised from the page, no shadow.
 *   INSET `bg-surface-container-low` + `border-border/40` — a lower region
 * inside a card; never another raised card.
 *   OVERLAY `bg-popover` — menus, dialogs and tooltips that occlude content.
 *
 * Typography, spacing and alignment still carry hierarchy inside each layer. * The fill only explains material depth, and every role resolves through the
 * same theme-aware token ladder used by the rest of the product.
 */
export const reportCard = {
  /** Standard raised content card. */
  section: 'rounded-lg border border-border/60 bg-card text-card-foreground',
  /** The same panel, clickable: state changes in both fill and edge. */
  interactive:
    'rounded-lg border border-border/60 bg-card text-card-foreground transition-colors hover:border-border hover:bg-surface-hover',
  /** Wider-radius raised wrapper for the major report/document shell.
   */
  hero: 'rounded-xl border border-border/60 bg-card text-card-foreground',
  /** Lower/inset region inside a card. */
  wire: 'rounded-lg border border-border/40 bg-surface-container-low',
  /** Clickable inset region. */
  wireInteractive:
    'rounded-lg border border-border/40 bg-surface-container-low transition-colors hover:border-border/60 hover:bg-surface-hover',
} as const;

/**
 * Report container — standard max-width wrapper for report content.
 *
 * Import this token instead of writing `mx-auto max-w-[1200px] px-6 sm:px-10`
 * directly. Use in cn() or template literals to compose with additional classes.
 */
export const reportContainer = 'mx-auto max-w-[1200px] px-6 sm:px-10';

/** Long-form column used by priority briefs and their surrounding chrome.
 *  Widened 960→1080 with the side padding halved (Max, 2026-07-29: the hero
 *  "looks like the text is wrapping too early. Maybe we have the margins be
 *  smaller on this page so we can show more") — the brief's middle layer is now
 *  mostly tables and a findings list, which use width far better than the old
 *  card stack did, and the hero headline gains a full word per line. Still a
 *  bounded measure: full-bleed prose at laptop widths is unreadable. */
export const editorialReportContainer = 'mx-auto max-w-[1080px] px-4 sm:px-6';

/**
 * Hosted-report layout rhythm.
 *
 * This is the spatial counterpart to `editorialText`: prose, examples, act
 * boundaries, and report Evidence each map to one named cadence instead of
 * rebuilding it with local `space-y-*`, `mt-*`, and `py-*` combinations.
 *
 * The resulting vertical ladder is intentional:
 *
 *   narrative beat       16px
 *   data-tab beat        20px
 *   narrative -> exhibit 24px (16px stack gap + 8px exhibit inset)
 *   act boundary         48px (16px stack gap + 32px header inset)
 *
 * The small 16 -> 24 -> 48 ladder keeps prose, evidence, and chapter changes
 * distinct without making every table feel like a new section. Keep the
 * document stack and its act/exhibit insets together so all report paths share
 * the same cadence.
 */
export const editorialRhythm = {
  /** The complete long-form report column. */
  document: 'space-y-4 py-8 text-pretty sm:py-12',
  /** Prose or component groups within an act. */
  narrative: 'space-y-4',
  /** A numbered act boundary inside `document`. */
  act: 'pt-8 text-balance',
  /** Tables, quotes, charts, and other evidence entering/leaving prose. */
  exhibit: 'py-2',
  /** Caption-to-exhibit relationship; deliberately tighter than prose. */
  exhibitCaption: 'mt-2',
  /** The dense Evidence area uses one repeated intro -> tabs -> panel beat. */
  dataDocument: 'space-y-5 py-8 sm:py-10',
} as const;

/*
 * `editorialMeasure` DELETED 2026-07-31 — ONE WIDTH, and the container is it.
 *
 * It existed for one day. The idea was a reading measure: cap prose at 44rem, let
 * exhibits run the container's full 1080, and let that WIDTH STEP be the
 * "story versus evidence" signal for free. The measure half was sound typography
 * (unbounded 16px prose over 1080px is ~138 characters a line, and the wall it
 * produced is what started this whole pass).
 *
 * On the page it did not read as a signal. It read as broken alignment. Max,
 * 2026-07-31, on two screenshots: *"we are not letting the text span all the width
 * available… Look at how wide the table goes, and then look at how early we wrap
 * the text. It seems like we have different widths for different text segments. We
 * should have, like, one for all of them."* He is right, and the reason the theory
 * failed is that the document ended up with FOUR right-hand edges, not two — prose
 * at 704px, the `<h1>` at 832, the pull quote at its own `36ch`, the answer excerpt
 * at `76ch`, and every table at 1080. Four arbitrary edges down one column do not
 * encode a distinction; they look like a bug, and the reader has no way to learn
 * which edge means what.
 *
 * What actually separates story from evidence is now doing the work on its own:
 * prose is short (611 words across 23 one-to-three-line paragraphs, down from
 * 1,145 across 48), and an exhibit is a TABLE — header row, rules, aligned
 * columns. Nobody mistakes one for the other. The measure was solving a problem
 * that the prose-volume cut had already removed.
 *
 * If long-form prose ever comes back to this document, the fix is fewer words, not
 * a narrower column. Do not reintroduce a per-element cap without re-reading this.
 */

/**
 * THE ONE LEFT EDGE INSIDE AN EXHIBIT — 12px, and it is `DataTable`'s, not ours.
 *
 * The tombstone above settles the RIGHT edge (there is one, and the container owns
 * it). This settles the left edge INSIDE an example. Max, 2026-08-03, on the Act 1
 * pull quote: *"that has the same margins on the right and left as the regular text,
 * but does not share the same left and right margins as the table examples, which it
 * should."*
 *
 * Measured, the brief had THREE left edges: prose at 0, table cell text at +12
 * (`DataTable`'s own `ROW_PADDING = 'px-3'`), and a linked pull quote back at 0
 * because its `-mx-3 px-3` cancelled out. An unlinked quote had no padding at all, so
 * one component rendered at two insets depending on whether the payload gave it an
 * href.
 *
 * The rule the brief now follows: **a hairline, a header rule or a track spans the
 * full measure; every line of TEXT inside an exhibit sits at the register's gutter.**
 * That is exactly what a `DataTable` row produces, and most exhibits in the document
 * ARE `DataTable`s — so the tables set the inset and everything else comes to them.
 *
 * **16px since 2026-08-13, and it MUST stay equal to `DataTable`'s `inset="wide"`
 * (`WIDE_ROW_PADDING`), which every report table now passes.** The two are one
 * decision written in two files: the report tables widened so the hover-revealed
 * drill mark has room in the gutter, and a caption still sitting at 12px would have
 * been the one line in the document out of step with the table above it — the exact
 * failure the paragraph above describes, reintroduced from the other side. If either
 * value moves again, move both in the same commit.
 *
 * NEVER put this on a wrapper that already contains a `DataTable` — it doubles to
 * 32px and that exhibit becomes the one thing on the page out of line again.
 */
export const EXHIBIT_TEXT_INSET = 'px-4';

/**
 * `HOVER_REVEAL_GROUP` — the NAMED hover group for a secondary affordance that
 * hides until hovered. Put it on the wrapper that holds the thing and its
 * affordance (a source identity + its open-in-a-new-tab icon, say) so the
 * affordance reveals when the reader hovers the THING, on a surface that has no
 * `DataTable` row to hover: a page header, a line of prose, a header chip row.
 *
 * A named group on purpose — the unnamed `group` is already spoken for by
 * `DataTable` rows and by several call sites that pass their own
 * `group-hover:underline` into a label, and `HOVER_REVEAL` listens for BOTH, so
 * a fresh unnamed group here would silently retarget those.
 */
export const HOVER_REVEAL_GROUP = 'group/reveal';

/**
 * `HOVER_REVEAL` — THE hover-reveal contract for a SECONDARY row affordance:
 * hidden at rest, shown while the reader is in the row or on the thing it
 * belongs to. This is the behaviour of `RowActionsMenu`'s kebab, row drill
 * chevrons, copy controls, and the Owner cell's "Third-party" label
 * (`ownerRevealClass`, which composes this).
 *
 * Four variants, and none of them is decorative — together they are what make
 * hiding an affordance legitimate rather than a trap:
 *   - **`group-hover` / `group-focus-within` (unnamed)** — every `DataTable` row
 *     is a `group`, so "hovering anywhere in that row" comes free.
 *   - **`HOVER_REVEAL_GROUP`** — the same two variants on the named group, for a
 *     local affordance with no row around it.
 *   - **`focus-visible`** — a real tab stop must
 *     reveal it even when no group ancestor exists at all.
 *   - **`[@media(hover:none)]`** — a touch pointer cannot hover, so it gets the
 *     affordance unconditionally. This is the half that was missing from the six
 *     hand-rolled `opacity-0 group-hover` anchors deleted in 2026-07-26, and the
 *     reason that clean-up chose "always visible" over fixing the reveal.
 *
 * It is OPACITY, never `hidden`/`w-0`: the icon keeps its box, so nothing shifts
 * when it appears (rule 67), and it stays in the accessibility tree, so a screen
 * reader reads it either way. An invisible icon also can't be clicked blind — a
 * pointer can only reach it by being inside the group that reveals it.
 *
 * The transition names BOTH properties (`transition-[opacity,color]`) rather than
 * `transition-opacity`, because these affordances also darken on hover and TWO
 * `transition-*` utilities on one element is a coin flip — they set the same
 * declaration, so whichever Tailwind emits last silently wins and the other
 * animation is dropped. Consumers must not add a second `transition-*`.
 */
export const HOVER_REVEAL = [
  'opacity-0 transition-[opacity,color]',
  'group-hover:opacity-100 group-focus-within:opacity-100',
  'group-hover/reveal:opacity-100 group-focus-within/reveal:opacity-100',
  'focus-visible:opacity-100',
  '[@media(hover:none)]:opacity-100',
].join(' ');

/**
 * A source's open-in-new-tab icon is narrower than a generic row affordance: it
 * appears only while the URL identity itself is hovered/focused. In particular,
 * it must not listen to an enclosing claim card or `DataTable` row's unnamed
 * `group`, because that makes the icon appear while the pointer is nowhere near
 * the URL. Every source identity + icon pair owns a `HOVER_REVEAL_GROUP` wrapper.
 *
 * Focus and touch keep the same accessibility escapes as `HOVER_REVEAL`.
 */
export const SOURCE_LINK_REVEAL = [
  'opacity-0 transition-[opacity,color]',
  'group-hover/reveal:opacity-100 group-focus-within/reveal:opacity-100',
  'focus-visible:opacity-100',
  '[@media(hover:none)]:opacity-100',
].join(' ');

/** URL-label side of the same source hover contract. */
export const SOURCE_LINK_TEXT_HOVER =
  'group-hover/reveal:underline group-hover/reveal:decoration-muted-foreground/40 group-hover/reveal:underline-offset-2 group-focus-within/reveal:underline';
